import {Injectable} from '@angular/core';
import {User} from './user.model';
import {Observable, of, Subject, throwError} from 'rxjs';
import {catchError, finalize, shareReplay, switchMap, take, tap} from 'rxjs/operators';
import {Principal} from './principal.service';
import {Router} from '@angular/router';
import { HttpClient } from '@angular/common/http';
import {AuthService as Auth0Service} from '@auth0/auth0-angular';
import {JwtHelperService} from '@auth0/angular-jwt';
import {UsersService} from '../../shared/services/users.service';
import {Publisher} from '../../shared/domain/publisher.model';
import {PublishersService} from '../../shared/services/publishers.service';
import {UserRole} from './user-role.model';
import {environment} from '../../../environments/environment';
import {ConsentService} from '../consent/consent.service';

/** Refresh a few seconds early so parallel API calls never race an expired token. */
const TOKEN_EXPIRY_SKEW_SECONDS = 60;

@Injectable({
  providedIn: 'root',
})
export class AuthService {

  updateUserHeaderData: Subject<boolean> = new Subject<boolean>();
  private readonly jwtHelper = new JwtHelperService();
  /** Coalesce concurrent Auth0 silent-token calls into one in-flight request. */
  private tokenRequest$: Observable<string | null> | null = null;

  constructor(private router: Router,
              private httpClient: HttpClient,
              private usersService: UsersService,
              private publishersService: PublishersService,
              private auth0Service: Auth0Service,
              private principal: Principal,
              private consentService: ConsentService) {
  }

  getToken() {
    return this.principal.getToken();
  }

  isAuthenticated() {
    return this.principal.isAuthenticated();
  }

  loginWithRedirect(returnUrl?: string) {
    this.auth0Service.loginWithRedirect({
      appState: {target: returnUrl || undefined},
    });
  }

  /**
   * Returns a usable access token. Prefers the in-memory Principal cache so every
   * HTTP interceptor hit does not wait on Auth0's getAccessTokenSilently (that was
   * adding noticeable latency to every page's data loads).
   */
  getAccessToken(): Observable<string | null> {
    const cached = this.principal.getToken();
    if (cached && !this.isTokenExpired(cached)) {
      return of(cached);
    }

    if (!this.tokenRequest$) {
      this.tokenRequest$ = this.auth0Service.getAccessTokenSilently({
        authorizationParams: {audience: environment.auth0.audience},
      }).pipe(
        tap((token) => {
          if (token) {
            this.principal.setToken(token);
          }
        }),
        catchError(() => of(null)),
        finalize(() => {
          this.tokenRequest$ = null;
        }),
        shareReplay(1),
      );
    }
    return this.tokenRequest$;
  }

  /** Drop the cached token so the next getAccessToken() refreshes from Auth0. */
  clearCachedToken(): void {
    this.tokenRequest$ = null;
    this.principal.setToken(null);
  }

  logout(): Observable<any> {
    return new Observable((observer) => {
      console.log('clearing authentication token');
      this.tokenRequest$ = null;
      this.principal.cleanup();
      this.consentService.clear();
      this.auth0Service.logout({
        logoutParams: {returnTo: window.location.origin, federated: true},
      });
      observer.complete();
    });
  }

  identity(force?: boolean): Observable<User> {
    return new Observable(subscriber => {
      const user = this.principal.getUser();
      if (!force && user) {
        const cachedToken = this.principal.getToken();
        if (cachedToken && !this.isTokenExpired(cachedToken)) {
          subscriber.next(user);
          subscriber.complete();
          return;
        }
        // User is known but token is missing/near-expiry — refresh token only (skip /users/me).
        this.getAccessToken().subscribe({
          next: (token) => {
            if (!token) {
              subscriber.error(new Error('Could not obtain access token'));
              return;
            }
            subscriber.next(user);
            subscriber.complete();
          },
          error: (error) => subscriber.error(error),
        });
        return;
      }
      this.principal.setUser(null);
      this.auth0Service.isAuthenticated$.pipe(
        take(1),
        switchMap(isAuth => {
          if (!isAuth) {
            return throwError(() => new Error('Not authenticated'));
          }
          return this.getAccessToken();
        }),
        switchMap(token => {
          if (!token) {
            return throwError(() => new Error('Could not obtain access token'));
          }
          this.principal.setToken(token);
          return this.usersService.me();
        })
      ).subscribe({
        next: newUser => {
          if (newUser.roles.includes(UserRole.ADMIN)) {
            this.principal.setMask(UserRole.ADMIN);
          }
          if (newUser.roles.includes(UserRole.SUPER_ADMIN)) {
            this.principal.setMask(UserRole.SUPER_ADMIN);
          }
          if (newUser.roles.includes(UserRole.READ_ONLY_ADMIN)) {
            this.principal.setMask(UserRole.READ_ONLY_ADMIN);
          }
          if (newUser.roles.includes(UserRole.USER)) {
            this.principal.setMask(UserRole.USER);
          }
          if (newUser.roles.includes(UserRole.READONLY_USER)) {
            this.principal.setMask(UserRole.READONLY_USER);
          }
          // SRS-47: auto-mask advertiser/finance only when no higher-priority main role is present.
          // USER/READONLY_USER are what the backend actually issues now (fold of
          // CAMPAIGN_MANAGER/DESIGNER and SALES_REPRESENTATIVE/REV_OPS respectively - see
          // user-role.model.ts); the legacy roles are never present in a real user's roles[].
          const hasHigherPriorityMainRole = newUser.roles.some(role => [
            UserRole.ADMIN,
            UserRole.SUPER_ADMIN,
            UserRole.READ_ONLY_ADMIN,
            UserRole.USER,
            UserRole.READONLY_USER,
          ].includes(role as UserRole));
          if (!hasHigherPriorityMainRole) {
            if (newUser.roles.includes(UserRole.ADVERTISER)) {
              this.principal.setMask(UserRole.ADVERTISER);
            } else if (newUser.roles.includes(UserRole.FINANCE)) {
              this.principal.setMask(UserRole.FINANCE);
            }
          }
          this.principal.setUser(newUser);
          this.consentService.refresh();
          subscriber.next(newUser);
          subscriber.complete();
        },
        error: (error) => subscriber.error(error),
      });
    });
  }

  publisherIdentity(force?: boolean): Observable<Publisher> {
    if (!force && this.principal.publisher) {
      // publisher exists and we didnt force identity - ok resolve it
      return of(this.principal.publisher);
    } else {
      const user = this.principal.getUser();
      if (user) {
        const publisherId = this.principal.mask.publisherId;
        if (!publisherId) {
          this.router.navigate(['login']);
          throw throwError('publisher id does not exist for user !');
        }
        return new Observable(subscriber => {
          this.publishersService.getPublisherById(publisherId)
            .subscribe(
              (publisher: Publisher) => {
                this.principal.publisher = publisher;
                subscriber.next(publisher);
              },
              () => subscriber.error(false),
              () => subscriber.complete());
        });
      } else {
        // ok resolve user and then try getting publisher identity again
        return new Observable(subscriber => {
          this.identity(true).subscribe(u => {
              this.publisherIdentity(true).subscribe(publisher => {
                  subscriber.next(publisher);
                },
                error => subscriber.error(error),
                () => subscriber.complete());
            },
            error => subscriber.error(error));
        });
      }
    }
  }

  private isTokenExpired(token: string): boolean {
    try {
      return this.jwtHelper.isTokenExpired(token, TOKEN_EXPIRY_SKEW_SECONDS);
    } catch {
      return true;
    }
  }
}
